import React, { useState, useEffect, useCallback } from 'react';
import { fetchReferralSummary, updateReferralCode, getHeaders } from '../api';
import { refundsState, netLabelTitle, refundedNote } from '../lib/netRevenue';

const API = import.meta.env.PROD ? '' : 'https://crm-api.squish.co';

// Local fetchers (kept out of api.js): create now carries profitSharePct,
// and economics is the click-through detail for one code.
async function createReferralCode(body) {
  const res = await fetch(`${API}/api/referrals/codes`, {
    method: 'POST', headers: getHeaders(), body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
  return data;
}
async function fetchCodeEconomics(id) {
  const res = await fetch(`${API}/api/referrals/codes/${encodeURIComponent(id)}/economics`, { headers: getHeaders() });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
  return data;
}
const fmt$ = (n) => `$${Math.round(Number(n) || 0).toLocaleString()}`;

// Launch referral bounty program — one row per registered partner code
// (Referral_Partner__c), plus any unregistered codes seen on Contacts.
// Payout owed = paying customers × flat bounty. Codes are created here and
// handed to reviewers as https://squish.co/?ref=<code> links; attribution is
// first-touch and permanent, so retiring a code only stops NEW attribution.
// See squish-docs/architecture/referral-tracking.md.

const LINK_BASE = 'https://squish.co/?ref=';

export default function Referrals() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [busyId, setBusyId]   = useState(null);
  const [copiedCode, setCopiedCode] = useState(null);
  // Click-through economics per code: expanded row id + fetched detail cache.
  const [expandedId, setExpandedId] = useState(null);
  const [econ, setEcon] = useState({});      // id → economics payload
  const [econBusy, setEconBusy] = useState(null);

  const toggleExpand = async (row) => {
    if (!row.registered) return;
    if (expandedId === row.id) { setExpandedId(null); return; }
    setExpandedId(row.id);
    if (!econ[row.id]) {
      setEconBusy(row.id);
      try {
        const detail = await fetchCodeEconomics(row.id);
        setEcon(prev => ({ ...prev, [row.id]: detail }));
      } catch (err) {
        setEcon(prev => ({ ...prev, [row.id]: { error: err.message } }));
      } finally {
        setEconBusy(null);
      }
    }
  };

  const load = useCallback(async () => {
    try {
      setError(null);
      setData(await fetchReferralSummary());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleActive = async (row) => {
    setBusyId(row.id);
    try {
      await updateReferralCode(row.id, { active: !row.active });
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyId(null);
    }
  };

  const copyLink = async (code) => {
    try {
      await navigator.clipboard.writeText(`${LINK_BASE}${code}`);
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(c => (c === code ? null : c)), 1500);
    } catch { /* clipboard unavailable — the link is visible in the row title */ }
  };

  if (loading) return <div style={{ padding: 32, color: 'var(--text2)' }}>Loading…</div>;
  if (error && !data) return <div style={{ padding: 32, color: 'var(--accent2)' }}>{error}</div>;

  const summary = data?.summary || [];
  // 'ok' = each code's revenue is net of refunds; 'unavailable' = gross.
  const refunds = refundsState(data);
  const totals = summary.reduce((t, r) => ({
    paying:  t.paying  + r.payingCustomers,
    revenue: t.revenue + r.revenue,
    owed:    t.owed    + r.owed,
  }), { paying: 0, revenue: 0, owed: 0 });

  return (
    <div style={{ padding: 32, maxWidth: 1100 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <div className="page-title">Referrals</div>
          <div className="page-subtitle">Launch bounty program — flat bounty per referred paying customer</div>
        </div>
        <button style={btnPrimary} onClick={() => setShowCreate(true)}>+ New code</button>
      </div>

      {error && <div style={{ color: 'var(--accent2)', marginBottom: 16 }}>{error}</div>}

      <div className="stat-grid" style={{ marginBottom: 24 }}>
        <div className="stat-card">
          <div className="stat-label">Referred signups</div>
          <div className="stat-value">{data?.totalReferredSignups ?? 0}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Paying customers</div>
          <div className="stat-value green">{totals.paying}</div>
        </div>
        <div className="stat-card">
          {/* Net of refunds since 2026-10-13 (lib/netRevenue.js). The label says
              which it is; a gross figure is never left under "net". */}
          <div className="stat-label" title={netLabelTitle(refunds)}>
            Referred revenue{refunds === 'ok' ? ' (net)' : refunds === 'unavailable' ? ' (gross)' : ''}
          </div>
          <div className="stat-value accent">${Math.round(totals.revenue).toLocaleString()}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Bounties owed</div>
          <div className="stat-value pink">${Math.round(totals.owed).toLocaleString()}</div>
        </div>
      </div>

      <div className="table-card">
        <div className="table-header">
          <span className="table-title">Codes</span>
          <span className="table-count">{summary.length}</span>
        </div>
        {summary.length === 0 ? (
          <div style={{ padding: 24, color: 'var(--text2)' }}>
            No codes yet. Create one and send the reviewer their link.
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Code</th><th>Partner</th><th>Bounty</th>
                <th>Signups</th><th>Paying</th><th title={netLabelTitle(refunds)}>Revenue</th><th>Owed</th>
                <th>Status</th><th></th>
              </tr>
            </thead>
            <tbody>
              {summary.map(row => (
                <React.Fragment key={row.id || row.code}>
                <tr
                  onClick={() => toggleExpand(row)}
                  style={row.registered ? { cursor: 'pointer' } : undefined}
                  title={row.registered ? 'Click for economics detail' : undefined}
                >
                  <td>
                    <span style={{ fontFamily: 'var(--mono)' }} title={`${LINK_BASE}${row.code}`}>{row.code}</span>
                    <button
                      style={copyBtn}
                      title={`Copy ${LINK_BASE}${row.code}`}
                      onClick={(e) => { e.stopPropagation(); copyLink(row.code); }}
                    >
                      {copiedCode === row.code ? '✓' : '⧉'}
                    </button>
                  </td>
                  <td>
                    {row.registered
                      ? <>
                          {row.partnerName}
                          {row.email && <div style={{ color: 'var(--text3)', fontSize: 12 }}>{row.email}</div>}
                        </>
                      : <span className="badge badge-yellow" title="A Contact carries this code but no partner record claims it — typo or rogue link">unregistered</span>}
                  </td>
                  <td>
                    {!row.registered ? '—' : (
                      [row.bounty ? `$${row.bounty}` : null, row.profitSharePct ? `${row.profitSharePct}% profit` : null]
                        .filter(Boolean).join(' + ') || <span style={{ color: 'var(--text3)' }}>unset</span>
                    )}
                  </td>
                  <td>{row.signups}</td>
                  <td style={{ color: row.payingCustomers > 0 ? 'var(--accent3)' : undefined }}>{row.payingCustomers}</td>
                  <td title={refundedNote({ state: refunds, gross: row.grossRevenue, refunded: row.refunded })?.title}>${Math.round(row.revenue).toLocaleString()}</td>
                  <td style={{ color: row.owed > 0 ? 'var(--accent2)' : undefined }}>
                    {row.owed > 0 ? `$${row.owed.toLocaleString()}` : '—'}
                  </td>
                  <td>
                    {row.registered && (
                      <span className={`badge ${row.active ? 'badge-green' : 'badge-gray'}`}>
                        {row.active ? 'active' : 'retired'}
                      </span>
                    )}
                  </td>
                  <td>
                    {row.registered && (
                      <button
                        style={btnGhost}
                        disabled={busyId === row.id}
                        onClick={(e) => { e.stopPropagation(); toggleActive(row); }}
                      >
                        {busyId === row.id ? '…' : row.active ? 'Retire' : 'Reactivate'}
                      </button>
                    )}
                  </td>
                </tr>
                {expandedId === row.id && (
                  <tr>
                    <td colSpan={9} style={{ background: 'var(--bg)', padding: '16px 20px' }}>
                      <EconomicsDetail detail={econ[row.id]} loading={econBusy === row.id} />
                    </td>
                  </tr>
                )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div style={{ color: 'var(--text3)', fontSize: 12, marginTop: 12 }}>
        Attribution is first-touch and permanent — retiring a code stops new attribution only.
        Eyeball recent refunds before paying a bounty.
      </div>

      {showCreate && (
        <CreateCodeModal
          onClose={() => setShowCreate(false)}
          onSaved={() => { setShowCreate(false); load(); }}
        />
      )}
    </div>
  );
}

// Expanded-row economics: cash earned, ESTIMATED profit, expected payout,
// per-customer + per-product breakdowns. All profit figures are estimates
// from family-level margin factors (crm-backend lib/referral-economics.js).
function EconomicsDetail({ detail, loading }) {
  if (loading || !detail) return <div style={{ color: 'var(--text3)', fontFamily: 'var(--mono)', fontSize: 12 }}>loading economics…</div>;
  if (detail.error) return <div style={{ color: 'var(--accent2)', fontFamily: 'var(--mono)', fontSize: 12 }}>{detail.error}</div>;
  const { partner, totals, customers, byProduct } = detail;
  const stat = (label, value, color) => (
    <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 10, padding: '12px 16px', minWidth: 150 }}>
      <div style={{ fontSize: 10, color: 'var(--text3)', fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 800, color: color || 'var(--text)' }}>{value}</div>
    </div>
  );
  return (
    <div>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
        {stat('Cash earned', fmt$(totals.revenue), 'var(--accent)')}
        {stat('Est. profit', fmt$(totals.estProfit), 'var(--accent3)')}
        {stat('Expected payout', fmt$(totals.expectedPayout), 'var(--accent2)')}
        {stat('Paying customers', totals.payingCustomers)}
        {stat('Signups', totals.signups)}
      </div>
      <div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--mono)', marginBottom: 12 }}>
        payout = {partner.bounty ? `$${partner.bounty} × ${totals.payingCustomers} paying` : null}
        {partner.bounty && partner.profitSharePct ? ' + ' : null}
        {partner.profitSharePct ? `${partner.profitSharePct}% × ${fmt$(totals.estProfit)} est. profit` : null}
        {!partner.bounty && !partner.profitSharePct ? 'no payout terms set on this code' : null}
        {' · profit is an estimate (family-level margin factors)'}
      </div>
      <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
        {customers.length > 0 && (
          <div style={{ minWidth: 280 }}>
            <div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6 }}>By customer</div>
            {customers.map(c => (
              <div key={c.accountId} style={{ display: 'flex', justifyContent: 'space-between', gap: 16, fontSize: 12, padding: '4px 0', borderBottom: '1px solid var(--row-border)' }}>
                <span>{c.name || c.accountId}</span>
                <span style={{ fontFamily: 'var(--mono)', color: 'var(--text2)' }}>{c.orders} ord · {fmt$(c.revenue)} rev · {fmt$(c.estProfit)} est</span>
              </div>
            ))}
          </div>
        )}
        {byProduct.length > 0 && (
          <div style={{ minWidth: 280 }}>
            <div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6 }}>By product</div>
            {byProduct.map(p => (
              <div key={p.code} style={{ display: 'flex', justifyContent: 'space-between', gap: 16, fontSize: 12, padding: '4px 0', borderBottom: '1px solid var(--row-border)' }}>
                <span style={{ fontFamily: 'var(--mono)' }}>{p.code}</span>
                <span style={{ fontFamily: 'var(--mono)', color: 'var(--text2)' }}>{fmt$(p.revenue)} rev · {fmt$(p.estProfit)} est ({p.marginPct}%)</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function CreateCodeModal({ onClose, onSaved }) {
  const [code, setCode]               = useState('');
  const [partnerName, setPartnerName] = useState('');
  const [email, setEmail]             = useState('');
  const [bounty, setBounty]           = useState('');
  const [profitShare, setProfitShare] = useState('20'); // 20% is the standard partner offer
  const [notes, setNotes]             = useState('');
  const [saving, setSaving]           = useState(false);
  const [error, setError]             = useState(null);

  const cleanCode = code.trim().toLowerCase();
  const codeOk = /^[a-z0-9_-]{2,32}$/.test(cleanCode);

  const handleSave = async () => {
    if (!codeOk) { setError('Code must be 2-32 chars: letters, numbers, dash, underscore.'); return; }
    if (!partnerName.trim()) { setError('Partner name is required.'); return; }
    if (bounty !== '' && (!Number.isInteger(Number(bounty)) || Number(bounty) < 0)) {
      setError('Bounty must be a whole-dollar amount.');
      return;
    }
    if (profitShare !== '' && (!Number.isFinite(Number(profitShare)) || Number(profitShare) < 0 || Number(profitShare) > 100)) {
      setError('Profit share must be 0-100.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await createReferralCode({
        code: cleanCode,
        partnerName: partnerName.trim(),
        email: email.trim() || undefined,
        bounty: bounty === '' ? undefined : Number(bounty),
        profitSharePct: profitShare === '' ? undefined : Number(profitShare),
        notes: notes.trim() || undefined,
      });
      onSaved?.();
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  return (
    <div style={backdrop} onClick={onClose}>
      <div style={panel} onClick={e => e.stopPropagation()}>
        <div style={header}>
          <span style={{ fontWeight: 600 }}>New referral code</span>
          <button style={closeBtn} onClick={onClose}>×</button>
        </div>
        <div style={body}>
          <label style={label}>Code</label>
          <input
            style={input} value={code} autoFocus
            placeholder="jane-yt"
            onChange={e => setCode(e.target.value)}
          />
          {cleanCode && (
            <div style={{ color: codeOk ? 'var(--text3)' : 'var(--accent2)', fontSize: 12, marginTop: 4 }}>
              {codeOk ? `Link: https://squish.co/?ref=${cleanCode}` : 'Letters, numbers, dash, underscore (2-32 chars)'}
            </div>
          )}
          <label style={label}>Partner name</label>
          <input style={input} value={partnerName} placeholder="Jane Doe (Hosting Reviews YT)" onChange={e => setPartnerName(e.target.value)} />
          <label style={label}>Partner email <span style={{ color: 'var(--text3)' }}>(optional)</span></label>
          <input style={input} value={email} placeholder="jane@example.com" onChange={e => setEmail(e.target.value)} />
          <label style={label}>Bounty per paying customer, whole $ <span style={{ color: 'var(--text3)' }}>(optional)</span></label>
          <input style={input} value={bounty} placeholder="75" inputMode="numeric" onChange={e => setBounty(e.target.value)} />
          <label style={label}>Profit share, % of estimated profit <span style={{ color: 'var(--text3)' }}>(optional — standard offer is 20)</span></label>
          <input style={input} value={profitShare} placeholder="20" inputMode="numeric" onChange={e => setProfitShare(e.target.value)} />
          <label style={label}>Notes <span style={{ color: 'var(--text3)' }}>(optional)</span></label>
          <textarea
            style={{ ...input, minHeight: 64, resize: 'vertical' }}
            value={notes} placeholder="Channel, audience size, agreed terms…"
            onChange={e => setNotes(e.target.value)}
          />
          {error && <div style={{ color: 'var(--accent2)', marginTop: 12 }}>{error}</div>}
        </div>
        <div style={footer}>
          <button style={btnSecondary} onClick={onClose} disabled={saving}>Cancel</button>
          <button data-write style={btnPrimary} onClick={handleSave} disabled={saving}>
            {saving ? 'Creating…' : 'Create code'}
          </button>
        </div>
      </div>
    </div>
  );
}

const backdrop = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 };
const panel    = { background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 12, width: 440, maxWidth: '100%', maxHeight: '90vh', display: 'flex', flexDirection: 'column' };
const header   = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBottom: '1px solid var(--border)' };
const body     = { padding: 18, overflowY: 'auto' };
const footer   = { display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '14px 18px', borderTop: '1px solid var(--border)' };
const closeBtn = { background: 'transparent', border: 'none', color: 'var(--text2)', fontSize: 18, cursor: 'pointer' };
const label    = { display: 'block', color: 'var(--text2)', fontSize: 12, margin: '14px 0 4px' };
const input    = { width: '100%', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text)', fontFamily: 'var(--font)', fontSize: 13, padding: '8px 10px', boxSizing: 'border-box' };
const btnPrimary   = { background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 14px', fontFamily: 'var(--font)', fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const btnSecondary = { background: 'var(--bg3)', color: 'var(--text2)', border: '1px solid var(--border)', borderRadius: 6, padding: '8px 14px', fontFamily: 'var(--font)', fontSize: 13, cursor: 'pointer' };
const btnGhost     = { background: 'transparent', color: 'var(--text2)', border: '1px solid var(--border)', borderRadius: 6, padding: '4px 10px', fontFamily: 'var(--font)', fontSize: 12, cursor: 'pointer' };
const copyBtn      = { background: 'transparent', border: 'none', color: 'var(--text3)', cursor: 'pointer', marginLeft: 6, fontSize: 13 };
